import type { GameConfig } from '../../../config/gameConfig';
import type { SettlementBuildingKey, Settlement } from '../types';

const CONFIG_BUILDING_KEYS: Record<SettlementBuildingKey, keyof GameConfig['buildings']> = {
    lumberjack: 'lumberjackCamp',
    sawmill: 'sawmill',
    farm: 'farm',
    huntingHut: 'huntingHut',
    fishingHut: 'fishingHut',
    smokehouse: 'smokehouse',
    weaver: 'weaver',
    blacksmith: 'blacksmith',
    goldMine: 'goldMine',
    mint: 'mint',
    caravanPost: 'caravanPost',
    stable: 'stable',
    quarry: 'quarry',
    masonry: 'masonry',
    charcoalPit: 'charcoalPit',
    ironMine: 'ironMine',
    smelter: 'smelter',
    barracks: 'barracks',
};

export function getConfigBuildingKey(
    buildingId: SettlementBuildingKey,
): keyof GameConfig['buildings'] {
    return CONFIG_BUILDING_KEYS[buildingId];
}

export function getBuildingStorage(settlement: Settlement, buildingId: SettlementBuildingKey) {
    return settlement.buildings[buildingId].storage;
}

export function getEffectiveInputCap(
    settlement: Settlement,
    config: GameConfig,
    buildingId: SettlementBuildingKey,
    resource: string,
): number {
    const configKey = getConfigBuildingKey(buildingId);
    const configCap = Math.max(0, config.buildings[configKey].input?.[resource]?.max ?? 0);
    const userCap = Math.max(0, settlement.buildings[buildingId].inputCap?.[resource] ?? configCap);
    if (configCap <= 0) return 0;
    return Math.min(userCap, configCap);
}

export function getEffectiveOutputCap(
    settlement: Settlement,
    config: GameConfig,
    buildingId: SettlementBuildingKey,
    resource: string,
): number {
    const configKey = getConfigBuildingKey(buildingId);
    const configCap = Math.max(0, config.buildings[configKey].output?.[resource]?.max ?? 0);
    const userCap = Math.max(
        0,
        settlement.buildings[buildingId].outputCap?.[resource] ?? configCap,
    );
    if (configCap <= 0) return 0;
    return Math.min(userCap, configCap);
}

export function setBuildingInputCap(
    settlement: Settlement,
    config: GameConfig,
    buildingId: SettlementBuildingKey,
    resource: string,
    value: number,
): number {
    const configKey = getConfigBuildingKey(buildingId);
    const configCap = Math.max(0, config.buildings[configKey].input?.[resource]?.max ?? 0);
    const clamped = Math.max(0, Math.min(value, configCap));
    settlement.buildings[buildingId].inputCap[resource] = clamped;
    return clamped;
}

export function setBuildingOutputCap(
    settlement: Settlement,
    config: GameConfig,
    buildingId: SettlementBuildingKey,
    resource: string,
    value: number,
): number {
    const configKey = getConfigBuildingKey(buildingId);
    const configCap = Math.max(0, config.buildings[configKey].output?.[resource]?.max ?? 0);
    const clamped = Math.max(0, Math.min(value, configCap));
    settlement.buildings[buildingId].outputCap[resource] = clamped;
    return clamped;
}
